import { useQuery } from '@tanstack/react-query';
import { Pose } from '../models';
import QueryKeys from '../utils/QueryKeys';

/**
 * EVERY photo the student was shot in this package's season(s), in the photographer's
 * favorite order — the set the senior gallery must show.
 *
 * usePoses() filters through Pose.for_package, which ends in `.where(number:
 * pose_numbers)` — the pose numbers the PRICELIST configures. Frames numbered above
 * that list are silently invisible to the customer. For a viewing/selection surface
 * that is the wrong question; this asks the complete-set one instead
 * (Pose.all_images_for_buy_all, via the complete_for_package filter).
 *
 * The purchase/build steps still use usePoses: there, pricelist config genuinely is
 * the right filter, because each pose slot carries its layout (background, position).
 */
export default function useAllSeniorPoses(studentId, packageId) {
  return useQuery(
    [QueryKeys.STUDENT_IMAGES, 'complete', studentId, packageId],
    () => Pose
      .where({ completeForPackage: [packageId, studentId] })
      // SPARSE FIELDSET, and it matters: every image URL the resource serializes is an
      // S3-signed URL, and Paperclip rebuilds an S3 client per call (~25ms EACH — see
      // #4546). A senior with 81 photos pays that per pose, per URL, so asking only for
      // the fields these screens render is what took the gallery from 8s to ~2s.
      //
      // image_url is the UNWATERMARKED pose, and it is here on purpose: the intro splash
      // shows the student's photos clean. It costs a second signed URL per pose (~2s on
      // an 81-photo senior) and that is a deliberate trade for how the intro looks.
      //
      // `folder` is an extra_attribute and must be opted into: getAppropriateBg matches a
      // pose's season against the package-poses spec, so without it a multi-season senior
      // package silently picks the wrong backdrop.
      .select(['number', 'url', 'extension', 'image_url', 'watermark_url', 'is_yearbook_pose', 'yearbook_acceptable', 'yearbook_attire'])
      .selectExtra(['folder'])
      .per(999)
      .all()
      .then((res) => res.data || []),
    { placeholderData: [], enabled: !!(studentId && packageId) },
  );
}
